import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

const SubscribeSuccess = () => {
  const { user, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [error, setError] = useState('');
  const hasProcessed = useRef(false); // Prevent multiple executions

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessed.current) {
      return;
    }

    // Wait for AuthContext to finish loading before proceeding
    if (loading) {
      return;
    }

    const upgradeUser = async () => {
      // Mark as processed to prevent re-execution
      hasProcessed.current = true;

      // Check if user is logged in by checking token
      const token = localStorage.getItem('token');
      if (!token) {
        // User not logged in, redirect to login
        setStatus('error');
        setError('You must be logged in to complete your subscription. Please log in and try again.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
        return;
      }

      // If user is null but token exists, try to refresh user first
      if (!user && token) {
        try {
          await refreshUser();
          // Wait a bit for user to be set
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (err) {
          console.error('Error refreshing user:', err);
          setStatus('error');
          setError('Unable to verify your account. Please log in and try again.');
          setTimeout(() => {
            navigate('/login');
          }, 3000);
          return;
        }
      }

      // Final check - if still no user, redirect to login
      if (!user) {
        setStatus('error');
        setError('You must be logged in to complete your subscription. Please log in and try again.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
        return;
      }

      try {
        setStatus('processing');
        
        // Set up axios default headers
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Upgrade user for 30 days (1 month subscription)
        const response = await axios.post(
          `${API_URL}/admin/upgrade-user`,
          { days: 30 },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (response.data.success) {
          setStatus('success');
          // Refresh user data to get updated premium status
          await refreshUser();
          
          // Redirect to upgrade success page
          setTimeout(() => {
            navigate('/upgrade-success', { replace: true });
          }, 1000);
        } else {
          setStatus('error');
          setError('Failed to upgrade account. Please contact support.');
        }
      } catch (err) {
        console.error('Upgrade error:', err);
        setStatus('error');
        const errorMessage = err.response?.data?.error || err.message || 'An error occurred while upgrading your account. Please try again or contact support.';
        setError(errorMessage);
        
        // If it's an authentication error, redirect to login
        if (err.response?.status === 401 || err.response?.status === 403) {
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        }
      }
    };

    // Only run when loading is complete
    if (!loading) {
      upgradeUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]); // Only depend on loading state - hasProcessed ref prevents re-execution

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 'calc(100vh - 80px)',
      padding: '2rem',
      textAlign: 'center'
    }}>
      {status === 'processing' && (
        <>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
          <h1>Processing Your Subscription...</h1>
          <p>Please wait while we upgrade your account.</p>
        </>
      )}
      
      {status === 'success' && (
        <>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
          <h1>Subscription Successful!</h1>
          <p>Your account has been upgraded to Premium. Redirecting to book search...</p>
        </>
      )}
      
      {status === 'error' && (
        <>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
          <h1>Subscription Error</h1>
          <p style={{ color: '#d32f2f', marginBottom: '1rem' }}>{error}</p>
          <button 
            onClick={() => navigate('/search')}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Go to Book Search
          </button>
        </>
      )}
    </div>
  );
};

export default SubscribeSuccess;
